<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <div>
          <h2>
            <!-- Updated icon logic to handle prerequisites -->
            <span v-if="complianceDetails?.isPrerequisite">📋</span>
            <span v-else-if="complianceDetails?.isMissing">🚨</span>
            <span v-else-if="driftDetails">📋</span>
            <span v-else>🔍</span>
            {{ policyName }}
          </h2>
          <p class="policy-path">
            {{ complianceDetails?.path || driftDetails?.path || "" }}
          </p>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="modal-body">
        <!-- Drift Tab: Show file-level changes -->
        <div v-if="driftDetails && !complianceDetails">
          <div class="drift-info">
            <h3>File Change Details</h3>

            <div class="change-info-box" :class="driftDetails.changeType">
              <div class="change-icon">
                <span v-if="driftDetails.changeType === 'add'">➕</span>
                <span v-else-if="driftDetails.changeType === 'edit'">📝</span>
                <span v-else-if="driftDetails.changeType === 'delete'">🗑️</span>
              </div>

              <div class="change-content">
                <h4>
                  <span v-if="driftDetails.changeType === 'add'"
                    >File Added</span
                  >
                  <span v-else-if="driftDetails.changeType === 'edit'"
                    >File Modified</span
                  >
                  <span v-else-if="driftDetails.changeType === 'delete'"
                    >File Deleted</span
                  >
                </h4>

                <p class="file-path">{{ driftDetails.path }}</p>

                <div class="drift-description">
                  <p v-if="driftDetails.changeType === 'add'">
                    This is a new configuration file that didn't exist in the
                    previous capture.
                  </p>
                  <p v-else-if="driftDetails.changeType === 'edit'">
                    This configuration file has been modified since the last
                    capture.
                  </p>
                  <p v-else-if="driftDetails.changeType === 'delete'">
                    This configuration file was present in the previous capture
                    but no longer exists.
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="driftDetails.propertyChangeCount > 0"
              class="property-changes"
            >
              <h4>
                Property Changes ({{ driftDetails.propertyChangeCount || 0 }})
              </h4>
              <div
                v-for="(change, index) in driftDetails.propertyChanges"
                :key="index"
                class="property-change-item"
              >
                <div class="property-header">
                  <span class="property-path">{{ change.property }}</span>
                  <span :class="['change-type', change.changeType]">{{
                    change.changeType.toUpperCase()
                  }}</span>
                </div>
                <div class="property-values">
                  <div
                    v-if="change.changeType !== 'added'"
                    class="value-box old"
                  >
                    <span class="value-label">Previous:</span>
                    <pre>{{ formatValue(change.oldValue) }}</pre>
                  </div>
                  <div
                    v-if="change.changeType !== 'removed'"
                    class="value-box new"
                  >
                    <span class="value-label">Current:</span>
                    <pre>{{ formatValue(change.newValue) }}</pre>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-note">
              ℹ️ This represents drift detection between captures. For detailed
              property comparisons against the baseline, check the "Baseline
              Compliance" section.
            </div>
          </div>
        </div>

        <!-- Compliance Tab: Show Property Deviations -->
        <div v-else-if="complianceDetails && complianceDetails.deviations">
          <div class="deviations-header">
            <h3>
              <span v-if="complianceDetails.isPrerequisite"
                >📋 Baseline Prerequisite</span
              >
              <span v-else-if="complianceDetails.isMissing"
                >⚠️ Missing Policy</span
              >
              <span v-else>Property Deviations</span>
            </h3>
            <span class="deviation-badge" :class="complianceDetails.severity">
              {{
                complianceDetails.deviationCount ||
                complianceDetails.deviations.length
              }}
              {{
                (complianceDetails.deviationCount ||
                  complianceDetails.deviations.length) === 1
                  ? "property"
                  : "properties"
              }}
            </span>
          </div>

          <!-- Selection Controls -->
          <div
            v-if="
              !complianceDetails.isMissing && !complianceDetails.isPrerequisite
            "
            class="selection-controls"
          >
            <label class="select-all-checkbox">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
              <span>Select All Deviations</span>
            </label>
            <div class="selection-info">
              {{ selectedDeviations.size }} of
              {{ complianceDetails.deviations.length }} selected
            </div>
          </div>

          <div class="deviations-list">
            <div
              v-for="(deviation, index) in complianceDetails.deviations"
              :key="index"
              :class="[
                'deviation-item',
                { 'missing-policy': deviation.isMissing },
                { 'prerequisite-policy': deviation.isPrerequisite },
                { selected: selectedDeviations.has(index) },
              ]"
            >
              <div class="deviation-header">
                <!-- Selection Checkbox -->
                <label
                  v-if="
                    !deviation.isMissing &&
                    !deviation.isPrerequisite &&
                    !complianceDetails.isMissing &&
                    !complianceDetails.isPrerequisite
                  "
                  class="deviation-checkbox"
                  @click.stop
                >
                  <input
                    type="checkbox"
                    :checked="selectedDeviations.has(index)"
                    @change="toggleDeviation(index)"
                  />
                </label>

                <span class="property-name">
                  {{
                    deviation.isPrerequisite
                      ? "📋 BASELINE PREREQUISITE"
                      : deviation.isMissing
                      ? "⚠️ MISSING POLICY"
                      : deviation.property
                  }}
                </span>
                <span
                  v-if="deviation.severity && !deviation.isPrerequisite"
                  :class="['severity-badge', deviation.severity]"
                >
                  {{ deviation.severity?.toUpperCase() }}
                </span>
              </div>

              <!-- Missing Policy Display -->
              <div v-if="deviation.isMissing" class="missing-policy-content">
                <div class="missing-icon">⚠️</div>
                <div class="missing-text">
                  <h4>Policy Missing from Client Configuration</h4>
                  <p>
                    This policy exists in the baseline but is not present in the
                    client's current configuration. The policy may have been
                    deleted or never implemented.
                  </p>
                  <div class="missing-details">
                    <div class="detail-row">
                      <span class="detail-label">Expected:</span>
                      <span class="detail-value">{{
                        deviation.baselineValue
                      }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Actual:</span>
                      <span class="detail-value error">{{
                        deviation.clientValue
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Prerequisite Policy Display -->
              <div
                v-else-if="deviation.isPrerequisite"
                class="prerequisite-policy-content"
              >
                <div class="prerequisite-icon">📋</div>
                <div class="prerequisite-text">
                  <h4>Baseline Prerequisite Policy</h4>
                  <p>
                    This policy is marked as a prerequisite in the baseline
                    configuration. It should be reviewed and implemented before
                    other dependent policies.
                  </p>
                  <div class="prerequisite-details">
                    <div class="detail-row">
                      <span class="detail-label">Policy Type:</span>
                      <span class="detail-value">{{
                        deviation.policyType || "Conditional Access"
                      }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Priority:</span>
                      <span class="detail-value priority">High</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Normal property deviation display -->
              <div v-else class="deviation-comparison">
                <div class="comparison-grid">
                  <!-- Baseline Column -->
                  <div class="comparison-column baseline-column">
                    <div class="column-header">
                      <span class="header-icon">✅</span>
                      <span class="header-text">BASELINE (RECOMMENDED)</span>
                    </div>
                    <div
                      class="value-container"
                      @click="
                        copyToClipboard(deviation.baselineValue, 'baseline')
                      "
                      :title="'Click to copy'"
                    >
                      <pre class="value-content">{{
                        formatValue(deviation.baselineValue)
                      }}</pre>
                      <div class="copy-indicator">
                        <span class="copy-icon">📋</span>
                        <span class="copy-text">Click to copy</span>
                      </div>
                    </div>
                  </div>

                  <!-- Arrow Separator -->
                  <div class="comparison-arrow">
                    <span>→</span>
                  </div>

                  <!-- Client Column -->
                  <div class="comparison-column client-column">
                    <div class="column-header">
                      <span class="header-icon">🔴</span>
                      <span class="header-text">CLIENT (CURRENT)</span>
                    </div>
                    <div
                      class="value-container"
                      @click="copyToClipboard(deviation.clientValue, 'client')"
                      :title="'Click to copy'"
                    >
                      <pre class="value-content">{{
                        formatValue(deviation.clientValue)
                      }}</pre>
                      <div class="copy-indicator">
                        <span class="copy-icon">📋</span>
                        <span class="copy-text">Click to copy</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <p class="no-data">No deviation details available.</p>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" @click="$emit('close')">Close</button>

        <!-- NEW: Fix Policy Button -->
        <button
          v-if="
            complianceDetails &&
            !complianceDetails.isMissing &&
            !complianceDetails.isPrerequisite
          "
          class="btn-fix"
          @click="handleFixSelectedDeviations"
          :disabled="selectedDeviations.size === 0 || isProcessing"
        >
          <span class="btn-icon">🔧</span>
          Fix
          {{
            selectedDeviations.size === complianceDetails.deviations.length
              ? "All"
              : "Selected"
          }}
          ({{ selectedDeviations.size }}) Deviation{{
            selectedDeviations.size !== 1 ? "s" : ""
          }}
        </button>

        <button
          v-if="complianceDetails?.isPrerequisite"
          class="btn-info"
          @click="handleAcknowledgePrerequisite"
        >
          Acknowledge Prerequisite
        </button>
        <button
          v-else-if="complianceDetails && !complianceDetails.isMissing"
          class="btn-primary"
          @click="handleAcceptSelectedDeviations"
          :disabled="selectedDeviations.size === 0 || isProcessing"
        >
          Accept
          {{
            selectedDeviations.size === complianceDetails.deviations.length
              ? "All"
              : "Selected"
          }}
          ({{ selectedDeviations.size }}) Deviation{{
            selectedDeviations.size !== 1 ? "s" : ""
          }}
        </button>
        <button
          v-else-if="complianceDetails?.isMissing"
          class="btn-warning"
          @click="handleAcknowledgeMissing"
        >
          Acknowledge Missing Policy
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { detectPolicyType } from "@/policy-similarity-matcher.js";

export default {
  name: "PolicyDetailModal",

  props: {
    policyName: String,
    driftDetails: Object,
    complianceDetails: Object,
    clientCode: String,
    branchName: String,
  },

  data() {
    return {
      copiedField: null,
      selectedDeviations: new Set(),
      isProcessing: false,
    };
  },

  computed: {
    allSelected() {
      if (!this.complianceDetails?.deviations) return false;
      return (
        this.selectedDeviations.size ===
        this.complianceDetails.deviations.length
      );
    },
  },

  mounted() {
    // Auto-select all deviations by default
    if (this.complianceDetails?.deviations) {
      this.complianceDetails.deviations.forEach((_, index) => {
        this.selectedDeviations.add(index);
      });
    }
  },

  methods: {
    formatValue(value) {
      if (value === null) return "null";
      if (value === undefined) return "undefined";
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    },

    async copyToClipboard(value, fieldType) {
      const text = this.formatValue(value);
      try {
        await navigator.clipboard.writeText(text);
        this.copiedField = fieldType;

        // Reset after 2 seconds
        setTimeout(() => {
          this.copiedField = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    },

    toggleDeviation(index) {
      if (this.selectedDeviations.has(index)) {
        this.selectedDeviations.delete(index);
      } else {
        this.selectedDeviations.add(index);
      }
      this.$forceUpdate();
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedDeviations.clear();
      } else {
        this.complianceDetails.deviations.forEach((_, index) => {
          this.selectedDeviations.add(index);
        });
      }
      this.$forceUpdate();
    },

    handleAcceptSelectedDeviations() {
      console.log("🔵 ========== MODAL: Accept Button Clicked ==========");
      console.log("🔵 Selected deviations Set:", this.selectedDeviations);
      console.log("🔵 Path:", this.complianceDetails?.path);
      console.log("🔵 Policy name:", this.policyName);

      const selectedDeviationObjects = Array.from(this.selectedDeviations).map(
        (index) => {
          const deviation = this.complianceDetails.deviations[index];
          console.log(`🔵 Selected deviation [${index}]:`, deviation);
          return deviation;
        }
      );

      const payload = {
        path: this.complianceDetails?.path,
        policyName: this.policyName,
        category: this.complianceDetails?.category,
        allDeviations: this.complianceDetails?.deviations,
        selectedDeviations: selectedDeviationObjects,
        selectedIndices: Array.from(this.selectedDeviations),
      };

      console.log("🔵 Emitting payload:", payload);
      console.log("🔵 Event name: 'accept-deviation'");

      this.$emit("accept-deviation", payload);

      this.$nextTick(() => {
        console.log("🔵 After nextTick - event should have reached parent");
      });
    },

    handleFixSelectedDeviations() {
      console.log("🔧 ========== MODAL: Fix Button Clicked ==========");
      console.log("🔧 Selected deviations Set:", this.selectedDeviations);
      console.log("🔧 Path:", this.complianceDetails?.path);
      console.log("🔧 Policy name:", this.policyName);
      console.log(
        "🔧 Baseline policy:",
        this.complianceDetails?.baselinePolicy
      );
      console.log("🔧 Client policy:", this.complianceDetails?.clientPolicy);

      // Prepare selected deviation details
      const selectedDeviationObjects = Array.from(this.selectedDeviations).map(
        (index) => {
          const deviation = this.complianceDetails.deviations[index];
          console.log(`🔧 Selected deviation [${index}]:`, deviation);
          return {
            property: deviation.property,
            baselineValue: deviation.baselineValue,
            clientValue: deviation.clientValue,
            severity: deviation.severity,
          };
        }
      );

      // Determine policy type from baseline policy
      const policyType = this.detectPolicyType(
        this.complianceDetails?.baselinePolicy
      );

      const payload = {
        // Policy identification
        path: this.complianceDetails?.path,
        policyName: this.policyName,
        category: this.complianceDetails?.category,
        policyType: policyType,

        // Policy objects
        baselinePolicy: this.complianceDetails?.baselinePolicy,
        clientPolicy: this.complianceDetails?.clientPolicy,

        // Deviations to fix
        selectedDeviations: selectedDeviationObjects,
        selectedIndices: Array.from(this.selectedDeviations),
        allDeviations: this.complianceDetails?.deviations,

        // Client context
        clientCode: this.clientCode,
        branchName: this.branchName,
      };

      console.log("🔧 Emitting fix-deviation payload:", payload);

      // Confirm with user before proceeding
      const confirmMessage =
        `Fix ${selectedDeviationObjects.length} deviation${
          selectedDeviationObjects.length !== 1 ? "s" : ""
        }?\n\n` +
        `This will:\n` +
        `1. Create a remediation branch\n` +
        `2. Update these properties to match baseline:\n` +
        selectedDeviationObjects.map((d) => `   • ${d.property}`).join("\n") +
        "\n\n" +
        `3. Trigger deployment pipeline\n` +
        `4. Require approval before applying to tenant\n\n` +
        `Continue?`;

      if (confirm(confirmMessage)) {
        this.isProcessing = true;
        this.$emit("fix-deviation", payload);

        console.log("🔧 After emit - waiting for parent to handle");

        // Reset processing state after a delay (parent should close modal on success)
        setTimeout(() => {
          this.isProcessing = false;
        }, 5000);
      } else {
        console.log("🔧 User cancelled fix operation");
      }
    },

    detectPolicyType,

    handleAcknowledgeMissing() {
      this.$emit("acknowledge-missing", {
        path: this.complianceDetails?.path,
        policyName: this.policyName,
        category: this.complianceDetails?.category,
      });
      this.$emit("close");
    },

    handleAcknowledgePrerequisite() {
      console.log("Prerequisite acknowledged");
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 1400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  border-bottom: 2px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.policy-path {
  margin: 0;
  font-size: 12px;
  color: #666;
  font-family: "Courier New", monospace;
  word-break: break-all;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #dc3545;
}

.modal-body {
  padding: 24px;
  overflow: auto;
  flex: 1;
}

/* Drift Display Styles */
.drift-info {
  padding: 20px;
}

.change-info-box {
  display: flex;
  gap: 20px;
  padding: 24px;
  border-radius: 8px;
  border-left: 4px solid;
  margin-bottom: 20px;
}

.change-info-box.add {
  background: #d4edda;
  border-left-color: #28a745;
}

.change-info-box.edit {
  background: #fff3cd;
  border-left-color: #ffc107;
}

.change-info-box.delete {
  background: #f8d7da;
  border-left-color: #dc3545;
}

.change-icon {
  font-size: 48px;
}

.change-content {
  flex: 1;
}

.change-content h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #1a1a1a;
}

.file-path {
  font-family: "Courier New", monospace;
  font-size: 13px;
  color: #666;
  margin: 8px 0;
  word-break: break-all;
}

.drift-description {
  margin-top: 12px;
}

.drift-description p {
  margin: 0;
  line-height: 1.6;
  color: #333;
}

.info-note {
  padding: 12px 16px;
  background: #e7f3ff;
  border-left: 3px solid #007bff;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
}

.property-changes {
  margin-top: 24px;
}

.property-changes h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #1a1a1a;
}

.property-change-item {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
}

.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.property-path {
  font-family: "Courier New", monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
}

.change-type {
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
}

.change-type.added {
  background: #d4edda;
  color: #155724;
}

.change-type.modified {
  background: #fff3cd;
  color: #856404;
}

.change-type.removed {
  background: #f8d7da;
  color: #721c24;
}

.property-values {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.value-box {
  padding: 12px;
  border-radius: 4px;
  border: 1px solid;
}

.value-box.old {
  background: #fff3cd;
  border-color: #ffc107;
}

.value-box.new {
  background: #d4edda;
  border-color: #28a745;
}

.value-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  display: block;
  margin-bottom: 4px;
}

.value-box pre {
  margin: 0;
  font-family: "Courier New", monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Selection Controls */
.selection-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 2px solid #e0e0e0;
}

.select-all-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
}

.select-all-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.selection-info {
  font-size: 13px;
  color: #666;
  font-weight: 600;
}

/* Compliance Display Styles */
.deviations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e0e0e0;
}

.deviations-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.deviation-badge {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  background: #f8f9fa;
  color: #666;
}

.deviations-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.deviation-item {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
}

.deviation-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.deviation-item.selected {
  border-color: #667eea;
  background: #f0f4ff;
}

.deviation-item.missing-policy {
  border: 3px solid #dc3545;
  background: #fff5f5;
}

.deviation-item.prerequisite-policy {
  border: 3px solid #0066cc;
  background: #e7f3ff;
}

.deviation-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  gap: 12px;
}

.deviation-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 0;
}

.deviation-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.property-name {
  font-weight: 600;
  font-size: 15px;
  color: #1a1a1a;
  font-family: "Courier New", monospace;
  word-break: break-word;
  flex: 1;
}

.severity-badge {
  padding: 4px 10px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.severity-badge.critical {
  background: #dc3545;
  color: white;
}

.severity-badge.high {
  background: #fd7e14;
  color: white;
}

.severity-badge.medium {
  background: #ffc107;
  color: #000;
}

.severity-badge.low {
  background: #28a745;
  color: white;
}

.severity-badge.prerequisite {
  background: #0066cc;
  color: white;
}

/* Missing Policy Content */
.missing-policy-content {
  display: flex;
  gap: 20px;
  padding: 24px;
  align-items: flex-start;
}

.missing-icon {
  font-size: 48px;
}

.missing-text {
  flex: 1;
}

.missing-text h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #721c24;
}

.missing-text p {
  margin: 0 0 16px 0;
  line-height: 1.6;
  color: #333;
}

.missing-details {
  background: white;
  border: 1px solid #dc3545;
  border-radius: 4px;
  padding: 12px;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 600;
  min-width: 80px;
  color: #666;
}

.detail-value {
  color: #1a1a1a;
}

.detail-value.error {
  color: #dc3545;
  font-weight: 600;
}

.detail-value.priority {
  color: #dc3545;
  font-weight: 600;
}

/* Prerequisite Policy Content */
.prerequisite-policy-content {
  display: flex;
  gap: 20px;
  padding: 24px;
  align-items: flex-start;
}

.prerequisite-icon {
  font-size: 48px;
}

.prerequisite-text {
  flex: 1;
}

.prerequisite-text h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #004085;
}

.prerequisite-text p {
  margin: 0 0 16px 0;
  line-height: 1.6;
  color: #333;
}

.prerequisite-details {
  background: white;
  border: 1px solid #0066cc;
  border-radius: 4px;
  padding: 12px;
}

/* Deviation Comparison */
.deviation-comparison {
  padding: 20px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 20px;
  align-items: start;
}

.comparison-column {
  border: 2px solid;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.baseline-column {
  border-color: #28a745;
}

.client-column {
  border-color: #dc3545;
}

.column-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.baseline-column .column-header {
  background: #d4edda;
  color: #155724;
}

.client-column .column-header {
  background: #f8d7da;
  color: #721c24;
}

.header-icon {
  font-size: 16px;
}

.value-container {
  padding: 16px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
}

.value-container:hover {
  background: #f8f9fa;
}

.value-container:hover .copy-indicator {
  opacity: 1;
}

.value-content {
  margin: 0;
  font-family: "Courier New", monospace;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.5;
}

.copy-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
}

.copy-icon {
  font-size: 14px;
}

.copy-text {
  font-size: 11px;
}

.comparison-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #666;
  margin-top: 40px;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 2px solid #e0e0e0;
}

.btn-primary,
.btn-secondary,
.btn-info,
.btn-warning,
.btn-fix {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover {
  background: #138496;
}

.btn-warning {
  background: #ffc107;
  color: #000;
}

.btn-warning:hover {
  background: #e0a800;
}

/* NEW: Fix Button Styling */
.btn-fix {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.btn-fix:hover:not(:disabled) {
  background: linear-gradient(135deg, #5568d3 0%, #654096 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.6);
  transform: translateY(-1px);
}

.btn-fix:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: none;
}

.btn-icon {
  font-size: 16px;
  line-height: 1;
}

.no-data {
  text-align: center;
  color: #666;
  padding: 40px;
  font-style: italic;
}
</style>
